import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, finalize, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AppState } from '../reducers';
import { loadOrderDefaultDataSuccessByKey } from './order-default-data.actions';
import { selectOrderDefaultDataClonedByKey, selectOrderDefaultDataHasDataByKey } from './order-default-data.selectors';
import { environment } from 'src/environments/environment';
import { GLOBAL_HEADERS } from '../store.constants';
import { ManageIdbService } from 'src/app/idb/manage-idb.service';
import { IdbOtCatalogName } from 'src/app/idb/idb-catalog-name.enum';
import { IdbOtDefaultDataEntry } from 'src/app/idb/idb.types';
import { fromIdbOtCatalog } from 'src/app/idb/idb-rxjs.helpers';

export interface OrderDefaultDataResponse {
  items: Array<{ countryId: number }>;
}

const ALL_KEY = 'ALL';

@Injectable({ providedIn: 'root' })
export class OrderDefaultDataApiService {
  private readonly api = environment.APIM + 'comorder/order' + '/GetOrderDefaultData';
  private pending: { [key: string]: Observable<OrderDefaultDataResponse> } = {};

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private idb: ManageIdbService,
  ) {}

  getAll(): Observable<OrderDefaultDataResponse> {
    const key = ALL_KEY;

    this.store
      .select(selectOrderDefaultDataHasDataByKey(key))
      .pipe(take(1))
      .subscribe((has) => {
        if (!has && !this.pending[key]) {
          this.pending[key] = this.hydrateFromIdbOrBackend(key).pipe(shareReplay(1), take(1));
          this.pending[key].subscribe();
        }
      });

    return this.store.select(selectOrderDefaultDataClonedByKey(key)).pipe(
      filter((resp) => !!resp),
      take(1),
    ) as Observable<OrderDefaultDataResponse>;
  }

  private hydrateFromIdbOrBackend(key: string): Observable<OrderDefaultDataResponse> {
    return fromIdbOtCatalog(this.idb, IdbOtCatalogName.OrderDefaultData).pipe(
      switchMap((idbEntry: IdbOtDefaultDataEntry | null) => {
        if (idbEntry?.data) {
          this.store.dispatch(loadOrderDefaultDataSuccessByKey({ key, response: idbEntry.data }));
          return of(idbEntry.data as OrderDefaultDataResponse);
        }
        return this.fetchFromBackend(key);
      }),
      catchError(() => this.fetchFromBackend(key)),
      finalize(() => {
        delete this.pending[key];
      }),
    );
  }

  private fetchFromBackend(key: string): Observable<OrderDefaultDataResponse> {
    return this.http.get<OrderDefaultDataResponse>(this.api, GLOBAL_HEADERS).pipe(
      take(1),
      tap((response) => {
        this.store.dispatch(loadOrderDefaultDataSuccessByKey({ key, response: response || {} }));
        this.idb
          .saveOtCatalog(IdbOtCatalogName.OrderDefaultData, { key: ALL_KEY, data: response })
          .catch(() => undefined);
      }),
      catchError(() => {
        const fallback: OrderDefaultDataResponse = { items: [] };
        this.store.dispatch(loadOrderDefaultDataSuccessByKey({ key, response: fallback }));
        return of(fallback);
      }),
    );
  }
}
